import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactModal from '../ContactModal/ContactModal';
import './Header.css';
import { useContactModal } from '../../../stores/modalStore';

interface HeaderProps {
  isScrolled: boolean;
  scrollProgress?: number; // 0 to 1, where 0 = top, 1 = scrolled down
}

const Header: React.FC<HeaderProps> = ({ isScrolled, scrollProgress = 0 }) => {
  const [isESolutionsOpen, setIsESolutionsOpen] = useState(false);
  const [isExpertiseOpen, setIsExpertiseOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Mobile dropdown states
  const [isMobileESolutionsOpen, setIsMobileESolutionsOpen] = useState(false);
  const [isMobileExpertiseOpen, setIsMobileExpertiseOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isMobileCompanyOpen, setIsMobileCompanyOpen] = useState(false);
  
  const { isOpen: isContactModalOpen, open: openContactModal, close: closeContactModal } = useContactModal();

  // Timeout refs for dropdown delays
  const eSolutionsTimeoutRef = useRef<number | null>(null);
  const expertiseTimeoutRef = useRef<number | null>(null);
  const servicesTimeoutRef = useRef<number | null>(null);
  const companyTimeoutRef = useRef<number | null>(null);

  // Helper function to handle dropdown hover with delay
  const handleDropdownEnter = (setter: (value: boolean) => void, timeoutRef: React.MutableRefObject<number | null>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setter(true);
  };

  const handleDropdownLeave = (setter: (value: boolean) => void, timeoutRef: React.MutableRefObject<number | null>) => {
    timeoutRef.current = setTimeout(() => {
      setter(false);
    }, 150); // 150ms delay before closing
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    const eSolutionsTimeout = eSolutionsTimeoutRef.current;
    const expertiseTimeout = expertiseTimeoutRef.current;
    const servicesTimeout = servicesTimeoutRef.current;
    const companyTimeout = companyTimeoutRef.current;
    
    return () => {
      if (eSolutionsTimeout) clearTimeout(eSolutionsTimeout);
      if (expertiseTimeout) clearTimeout(expertiseTimeout);
      if (servicesTimeout) clearTimeout(servicesTimeout);
      if (companyTimeout) clearTimeout(companyTimeout);
    };
  }, []);

  const eSolutionsOptions = [
    { name: 'eClinical\nConsulting', href: '#eclinical-consulting' },
    { name: 'eCOA Product\nSupport', href: '#ecoa-support' },
    { name: 'Data Monitoring &\nBiometrics', href: '#data-monitoring' },
    { name: 'Change\nManagement', href: '#change-management' },
    {
      name: 'Clinical Operations\nObservation & Management',
      href: '#clinical-operations',
    },
    { name: 'Clinical Trial\nManagement', href: '#clinical-trials' },
  ];

  const expertiseOptions = [
    { name: 'Medical Affairs', href: '#medical-affairs' },
    { name: 'Pharmacovigilance', href: '#pharmacovigilance' },
    { name: 'Evidence Generation', href: '#evidence-generation' },
    { name: 'Regulatory Documentation', href: '#regulatory-documentation' },
  ];

  const servicesOptions = [
    { name: 'User Acceptance\nTesting', href: '#user-acceptance-testing' },
    { name: 'License\nManagement', href: '#license-management' },
    { name: 'eCOA Rescue\nProject', href: '#ecoa-rescue-project' },
    { name: 'Staff\nAugmentation', href: '#staff-augmentation' },
    { name: 'Consultancy', href: '#consultancy' },
  ];

  const companyOptions = [
    { name: 'About Us', href: '/about-us', isPage: true },
    { name: 'Contact', href: '#contact', isModal: true },
    { name: 'Our Blogs', href: '/blogs', isPage: true },
    { name: 'Case Studies', href: '/case-studies', isPage: true },
  ];

  // Calculate dynamic styles based on scroll progress
  const getNavStyles = () => {
    // Check if we're on mobile (screen width < 768px)
    const isMobile = window.innerWidth < 768;
    
    // If mobile, return default styles without shrinking
    if (isMobile) {
      return {
        transform: 'scale(1)',
        padding: '0.75rem 1rem', // Mobile padding
        maxWidth: '100%', // Full width on mobile
      };
    }
    
    // Desktop shrinking effect
    const scale = 1 - (scrollProgress * 0.05);
    const paddingReduction = scrollProgress * 0.25;
    const horizontalPadding = 2.5 - (paddingReduction * 0.5);
    const verticalPadding = 1.25 - (paddingReduction * 0.25);
    
    return {
      transform: `scale(${scale})`,
      padding: `${verticalPadding}rem ${horizontalPadding}rem`,
      maxWidth: `${1600 - (scrollProgress * 200)}px`,
    };
  };

  return (
    <header className={`cinquity-header ${isScrolled ? 'scrolled' : ''}`}>
      <nav className='cinquity-nav' style={getNavStyles()}>
        <div className='nav-brand'>
          <Link to='/' className='logo-container'>
            <img
              src='/Cinquity Logo Colored.png'
              alt='Cinquity'
              className='logo-image'
            />
          </Link>
        </div>
        <div className='nav-menu'>
          <div
            className='nav-dropdown'
            onMouseEnter={() => handleDropdownEnter(setIsESolutionsOpen, eSolutionsTimeoutRef)}
            onMouseLeave={() => handleDropdownLeave(setIsESolutionsOpen, eSolutionsTimeoutRef)}
          >
            <a href='#services' className='nav-link dropdown-trigger'>
              E-Solutions
              <span className='dropdown-arrow'>▼</span>
            </a>
            {isESolutionsOpen && (
              <div className='dropdown-menu'>
                <div className='dropdown-content'>
                  {eSolutionsOptions.map((option, index) => (
                    <a key={index} href={option.href} className='dropdown-item'>
                      <span className='dropdown-bullet'>▶</span>
                      <span className='dropdown-text'>
                        {option.name.split('\n').map((line, i) => (
                          <span key={i}>
                            {line}
                            {i === 0 && <br />}
                          </span>
                        ))}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            className='nav-dropdown'
            onMouseEnter={() => handleDropdownEnter(setIsExpertiseOpen, expertiseTimeoutRef)}
            onMouseLeave={() => handleDropdownLeave(setIsExpertiseOpen, expertiseTimeoutRef)}
          >
            <a href='#expertise' className='nav-link dropdown-trigger'>
              Expertise
              <span className='dropdown-arrow'>▼</span>
            </a>
            {isExpertiseOpen && (
              <div className='dropdown-menu'>
                <div className='dropdown-content'>
                  {expertiseOptions.map((option, index) => (
                    <a key={index} href={option.href} className='dropdown-item'>
                      <span className='dropdown-bullet'>▶</span>
                      <span className='dropdown-text'>{option.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            className='nav-dropdown'
            onMouseEnter={() => handleDropdownEnter(setIsServicesOpen, servicesTimeoutRef)}
            onMouseLeave={() => handleDropdownLeave(setIsServicesOpen, servicesTimeoutRef)}
          >
            <a href='#services' className='nav-link dropdown-trigger'>
              Services
              <span className='dropdown-arrow'>▼</span>
            </a>
            {isServicesOpen && (
              <div className='dropdown-menu'>
                <div className='dropdown-content'>
                  {servicesOptions.map((option, index) => (
                    <a key={index} href={option.href} className='dropdown-item'>
                      <span className='dropdown-bullet'>▶</span>
                      <span className='dropdown-text'>
                        {option.name.split('\n').map((line, i) => (
                          <span key={i}>
                            {line}
                            {i === 0 && <br />}
                          </span>
                        ))}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            className='nav-dropdown'
            onMouseEnter={() => handleDropdownEnter(setIsCompanyOpen, companyTimeoutRef)}
            onMouseLeave={() => handleDropdownLeave(setIsCompanyOpen, companyTimeoutRef)}
          >
            <a href='#company' className='nav-link dropdown-trigger'>
              Company
              <span className='dropdown-arrow'>▼</span>
            </a>
            {isCompanyOpen && (
              <div className='dropdown-menu'>
                <div className='dropdown-content'>
                  {companyOptions.map((option, index) => (
                    option.isPage ? (
                      <Link 
                        key={index} 
                        to={option.href} 
                        className="dropdown-item"
                        onClick={() => setIsCompanyOpen(false)}
                      >
                        <span className="dropdown-bullet">▶</span>
                        <span className="dropdown-text">{option.name}</span>
                      </Link>
                    ) : (
                      <a 
                        key={index} 
                        href={option.href} 
                        className="dropdown-item"
                        onClick={(e) => {
                          if (option.isModal) {
                            e.preventDefault();
                            openContactModal();
                            setIsCompanyOpen(false);
                          }
                        }}
                      >
                        <span className="dropdown-bullet">▶</span>
                        <span className="dropdown-text">{option.name}</span>
                      </a>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='header-controls'>
          <button
            className='cta-button'
            onClick={openContactModal}
          >
            Request Demo
          </button>
          <button
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className='mobile-menu-overlay active'
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className='mobile-nav-links'>
          {/* E-Solutions */}
          <div className='mobile-dropdown-section'>
            <button 
              className='nav-link mobile-dropdown-trigger'
              onClick={() => setIsMobileESolutionsOpen(!isMobileESolutionsOpen)}
            >
              E-Solutions
              <span className={`mobile-dropdown-arrow ${isMobileESolutionsOpen ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-items ${isMobileESolutionsOpen ? 'mobile-dropdown-items-open' : ''}`}>
              {eSolutionsOptions.map((option, index) => (
                <a key={index} href={option.href} className='mobile-dropdown-item'>
                  <span className='mobile-dropdown-bullet'>▶</span>
                  <span className='mobile-dropdown-text'>
                    {option.name.split('\n').map((line, i) => (
                      <span key={i}>
                        {line}
                        {i === 0 && <br />}
                      </span>
                    ))}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Expertise */}
          <div className='mobile-dropdown-section'>
            <button 
              className='nav-link mobile-dropdown-trigger'
              onClick={() => setIsMobileExpertiseOpen(!isMobileExpertiseOpen)}
            >
              Expertise
              <span className={`mobile-dropdown-arrow ${isMobileExpertiseOpen ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-items ${isMobileExpertiseOpen ? 'mobile-dropdown-items-open' : ''}`}>
              {expertiseOptions.map((option, index) => (
                <a key={index} href={option.href} className='mobile-dropdown-item'>
                  <span className='mobile-dropdown-bullet'>▶</span>
                  <span className='mobile-dropdown-text'>{option.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className='mobile-dropdown-section'>
            <button 
              className='nav-link mobile-dropdown-trigger'
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
            >
              Services
              <span className={`mobile-dropdown-arrow ${isMobileServicesOpen ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-items ${isMobileServicesOpen ? 'mobile-dropdown-items-open' : ''}`}>
              {servicesOptions.map((option, index) => (
                <a key={index} href={option.href} className='mobile-dropdown-item'>
                  <span className='mobile-dropdown-bullet'>▶</span>
                  <span className='mobile-dropdown-text'>
                    {option.name.split('\n').map((line, i) => (
                      <span key={i}>
                        {line}
                        {i === 0 && <br />}
                      </span>
                    ))}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div className='mobile-dropdown-section'>
            <button 
              className='nav-link mobile-dropdown-trigger'
              onClick={() => setIsMobileCompanyOpen(!isMobileCompanyOpen)}
            >
              Company
              <span className={`mobile-dropdown-arrow ${isMobileCompanyOpen ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`mobile-dropdown-items ${isMobileCompanyOpen ? 'mobile-dropdown-items-open' : ''}`}>
              {companyOptions.map((option, index) => (
                option.isPage ? (
                  <Link 
                    key={index} 
                    to={option.href} 
                    className="mobile-dropdown-item"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="mobile-dropdown-bullet">▶</span>
                    <span className="mobile-dropdown-text">{option.name}</span>
                  </Link>
                ) : (
                  <a 
                    key={index} 
                    href={option.href} 
                    className="mobile-dropdown-item"
                    onClick={(e) => {
                      if (option.isModal) {
                        e.preventDefault();
                        openContactModal();
                        setIsMobileMenuOpen(false);
                      }
                    }}
                  >
                    <span className="mobile-dropdown-bullet">▶</span>
                    <span className="mobile-dropdown-text">{option.name}</span>
                  </a>
                )
              ))}
            </div>
          </div>
        </div>
        <button
          className='cta-button'
          onClick={() => {
            openContactModal();
            setIsMobileMenuOpen(false);
          }}
        >
          Request Demo
        </button>
      </div>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
      />
    </header>
  );
};

export default Header;
